import { toInteger } from '@repo/money'
import type { InventoryItemDto } from 'src/features/inventory/types/dto'

export type DebugItemType = 'free_bet' | 'booster'

export interface BuildDebugInventoryParams {
  /** What kind of item to mint. */
  type: DebugItemType
  /** How many separate items to mint (mirrors how a real grant arrives — one item per bet). */
  count: number
  /** `claimable` reproduces the real "just granted" grant flow (banner + auto-claim); `claimed` drops them straight in as usable. */
  status: 'claimable' | 'claimed'
  /** Empty array = applies to ALL games (the platform convention). */
  applicableGames: string[]
  /** Stable-ish seed so ids are unique per grant but deterministic within a build (kept pure — supplied by the caller). */
  seed: string
  /** Current time as ISO, injected for purity/testability. */
  nowIso: string
  playerId?: string
  // free_bet
  /** Per-bet value in DISPLAY units (e.g. 5 for $5). Converted to the wire integer the operator would actually send. */
  amount?: number
  currency?: string
  // booster
  boostValue?: number
  boostUnit?: 'percentage' | 'fixed'
  activationType?: 'auto' | 'manual'
  // common
  /** null = no expiry. A positive number sets `expiresAt` that many hours from `nowIso`. */
  expiresInHours?: number | null
}

/**
 * Pure builder that mints fake inventory DTOs identical in shape to what the
 * realtime server sends over `inventory-item:added`. Returning DTOs (not domain
 * items) means callers run them through the real `fromInventoryItemDTO` mapper,
 * so the money conversion and validation paths are exercised exactly as in prod.
 */
export function buildDebugInventoryDtos(params: BuildDebugInventoryParams): InventoryItemDto[] {
  const {
    type,
    count,
    status,
    applicableGames,
    seed,
    nowIso,
    playerId = 'debug-player',
    amount = 5,
    currency = 'USD',
    boostValue = 15,
    boostUnit = 'percentage',
    activationType = 'manual',
    expiresInHours = null
  } = params

  const n = Math.max(1, Math.floor(count))
  const claimedAt = status === 'claimed' ? nowIso : null
  const expiresAt =
    expiresInHours === null
      ? null
      : new Date(new Date(nowIso).getTime() + expiresInHours * 3_600_000).toISOString()

  return Array.from({ length: n }, (_, i): InventoryItemDto => {
    const base = {
      id: `debug-${type}-${seed}-${i}`,
      playerId,
      status,
      createdAt: nowIso,
      expiresAt,
      claimedAt,
      applicableGames,
      // `debug` origin lets the rest of the app recognise injected items
      // (e.g. so auto-claim can resolve them locally instead of hitting the backend).
      tags: ['debug'],
      originType: 'debug',
      originId: `debug-${seed}`
    }

    if (type === 'booster') {
      return {
        ...base,
        type: 'booster',
        boost: { value: boostValue, unit: boostUnit },
        activationType
      }
    }

    return {
      ...base,
      type: 'free_bet',
      amount: toInteger(amount, currency),
      currency
    }
  })
}
